// Copyright (c) Docugami, Inc. All rights reserved.

import { ComponentId, tagComponent } from "sdv/engine/Component";
import { Entity, IEntityManager } from "sdv/engine/Entity";
import { ISystem, Queries } from "../engine/Engine";
import { IResources } from "../Resources";
import { InputState } from "sdv/input/InputController";
import { ActionId } from "sdv/input/Actions";
import { IExternalCallbacks } from "sdv/ExternalCallbacks";
import { CanvasSelectionMode } from "store/documents/types";
import { TokenRangeComponent } from "sdv/components/TokenRangeComponent";
import { CursorComponent } from "sdv/components/CursorComponent";
import { TokenRenderComponent } from "sdv/components/TokenRenderComponent";
import { editableTokenColor, hoveredTokenColor, selectedEditTokenColor } from "components/shell/presentation/AppStyles";
import { TokenRange } from "networking/api";
import { TokenIdToBox, boxesEqual } from "store/shared_types";
import { ExternalRenderComponent } from "sdv/components/ExternalRenderComponent";
import { PositionComponent } from "sdv/components/PositionComponent";
import { TextEditorComponent } from "sdv/components/TextEditorComponent";

/**
 * Manages the lifetime of a text edit operation by creating and updating token edit state
 * with a token editor singleton, and manages the selected control token render
 */
export class TextEditSystem implements ISystem {

    get updateEnabled() { return true; }

    queries: Queries = {
        cursor: { types: [ComponentId.Cursor] },
        selectedControl: { types: [ComponentId.Selected, ComponentId.TokenRange, ComponentId.Control] },
        tokenRenderControls: { types: [ComponentId.TokenRender, ComponentId.Control] },
        textEditor: { types: [ComponentId.TextEditor, ComponentId.ExternalRender] },
        editSelection: { types: [ComponentId.EditTextSelectionComponent, ComponentId.TokenRange] }
    }

    input!: InputState;
    entities!: IEntityManager;
    resources!: IResources;
    callbacks!: IExternalCallbacks;

    private _textEditActive: boolean = false;

    update(): void {
        this.handleStateReset();
        this.handleTextEdit();
    }

    private handleStateReset() {
        const textEditEnabled = this.resources.get("tool").value === CanvasSelectionMode.TextEdit;
        const selectedControl = this.queries.selectedControl.query!.entities[0];
        const activeEditor = this.queries.textEditor.query!.single();
        const editSelection = this.queries.editSelection.query!.single();

        if (this._textEditActive && (!selectedControl || !textEditEnabled || !selectedControl.hasComponent(ComponentId.TokenRender))) {
            this.resources.set("controlRenderDisabled", false);
            selectedControl?.ensureComponent(tagComponent(ComponentId.Renderable));
            // Destroy any entities or components created for the text edit operation
            this.queries.tokenRenderControls.query!.entities.forEach(entity => entity.removeComponent(ComponentId.TokenRender));
            activeEditor?.destroy();
            editSelection?.destroy();
            this._textEditActive = false;
        }
    }

    private handleTextEdit() {
        const textEditEnabled = this.resources.get("tool").value === CanvasSelectionMode.TextEdit;
        const selectedControl = this.queries.selectedControl.query!.entities[0];
        let activeEditor = this.queries.textEditor.query!.single();

        if (!!selectedControl && textEditEnabled) {
            if (!activeEditor) {
                // Set up initial token editor state
                activeEditor = createDefaultTextEditorEntity(this.entities);

                selectedControl.removeComponent(ComponentId.Renderable);
                this.resources.set("controlRenderDisabled", true);
                this._textEditActive = true;
            }

            const tokenLookup = this.resources.get("pageTokens").value;

            let activeEditorComp = activeEditor!.getComponent<TextEditorComponent>(ComponentId.TextEditor)!;

            // Capture the current hovered, pressed, and selected tokens
            const selectState = this.input.getButton(ActionId.Select);
            const cursorToken = this.getCursorTokenWithinControl(selectedControl, tokenLookup);
            const hoveredToken = cursorToken;
            let selectedToken = undefined;

            if (selectState.isDown && !selectState.handled) {
                activeEditorComp.pressedToken = cursorToken;
                if (activeEditorComp.pressedToken !== undefined) {
                    selectState.handle();
                }
            } else if (selectState.isUp) {
                selectedToken = undefined;
                if (activeEditorComp.pressedToken !== undefined && activeEditorComp.pressedToken === cursorToken) {
                    selectState.handle();
                    selectedToken = cursorToken;
                }
                activeEditorComp.pressedToken = undefined;
            }

            // Tracks whether the hovered or selected token has changed
            let shouldUpdate = false;

            const editSelectionEntity = this.queries.editSelection.query!.single();
            let editTokenRange = editSelectionEntity?.getComponent<TokenRangeComponent>(ComponentId.TokenRange)!.tokenRange;

            // Once set, only update the selected token in the editor when it is valid
            if (selectedToken !== undefined && editTokenRange?.fromTok !== selectedToken) {
                // We're changing the edit token range since the selected token has changed
                editSelectionEntity?.destroy();
                let toTok = selectedToken;
                do {
                    toTok++;
                } while(boxesEqual(tokenLookup[selectedToken], tokenLookup[toTok]));

                activeEditorComp = {
                    ...activeEditorComp,
                    hoveredToken: hoveredToken
                };
                editTokenRange = { fromTok: selectedToken, toTok: toTok };

                activeEditor!.ensureComponent<TextEditorComponent>(activeEditorComp, true);
                this.entities.create()
                    .addComponent(tagComponent(ComponentId.EditTextSelectionComponent))
                    .addComponent<TokenRangeComponent>({
                        typeId: ComponentId.TokenRange,
                        tokenRange: editTokenRange
                    })

                // Overwrite the position component, since the selected token has changed
                const tokenBox = tokenLookup[selectedToken];
                activeEditor!.ensureComponent<PositionComponent>({
                    typeId: ComponentId.Position,
                    x: tokenBox.left,
                    y: tokenBox.top - 40
                }, true)

                // Render the editor to the canvas
                activeEditor!.ensureComponent(tagComponent(ComponentId.Renderable));
                shouldUpdate = true;
            } else if (hoveredToken !== activeEditorComp.hoveredToken) {
                // The hovered token data is only used within the ECS, and not needed within the react editor component
                // So when this changes, we can update the entity's component data in-place
                activeEditorComp.hoveredToken = hoveredToken;
                shouldUpdate = true;
            }

            // We only need to update the token color overrides, and overwrite the token render
            // component data, when the hovered or selected token changes while editing
            const tokenColor: Record<number, string> = {};
            if (shouldUpdate) {
                if (activeEditorComp.hoveredToken !== undefined) {
                    tokenColor[activeEditorComp.hoveredToken] = hoveredTokenColor;
                }

                if (editTokenRange !== undefined) {
                    for (let i = editTokenRange.fromTok; i < editTokenRange.toTok; ++i) {
                        tokenColor[i] = selectedEditTokenColor;
                    }
                }
            }

            selectedControl.ensureComponent<TokenRenderComponent>({
                typeId: ComponentId.TokenRender,
                defaultColor: editableTokenColor,
                tokenColor: tokenColor
            }, shouldUpdate);
        }
    }

    // Gets the valid cursor token that exists within the given control's token range
    // If none exists, returns undefined
    private getCursorTokenWithinControl(control: Entity, tokenLookup: TokenIdToBox) {
        const tokenRange = control.getComponent<TokenRangeComponent>(ComponentId.TokenRange)!.tokenRange;
        const cursorToken = this.queries.cursor.query!.single()?.getComponent<CursorComponent>(ComponentId.Cursor)!.token;
        return getValidTokenWithinRangeOrEmpty(cursorToken, tokenRange, tokenLookup);
    }
}

function createDefaultTextEditorEntity(entities: IEntityManager) {
    return entities.create()
        .addComponent<TextEditorComponent>({
            typeId: ComponentId.TextEditor
        })
        .addComponent<ExternalRenderComponent>({
            typeId: ComponentId.ExternalRender,
            size: {
                width: 155,
                height: 30
            }
        });
}

function getValidTokenWithinRangeOrEmpty(token: number | undefined, tokenRange: TokenRange, tokenLookup: TokenIdToBox): number | undefined {
    if (token !== undefined && !!tokenLookup[token] && tokenIsWithinRange(token, tokenRange)) {
        return token;
    }
}

function tokenIsWithinRange(token: number, tokenRange: TokenRange): boolean {
    return token >= tokenRange.fromTok && token < tokenRange.toTok;
}
